import Loader from "@/components/Loader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BiSolidHelpCircle } from "react-icons/bi";
import { Eye, InfoIcon } from "lucide-react";
import { Layers2 } from "lucide-react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { API_URL } from "@/config";
import {
  PaginationButton,
  formatDate,
  formatToDecimalPlaces,
  getIsFxStrategy,
  randomString,
} from "@/helpers/helperFunctions";
import { cn } from "@/lib/utils";
import axios from "axios";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { IoClose, IoFilterSharp } from "react-icons/io5";
import Slider from "react-slick";
import ViewModal from "./StrategyViewModal";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { MdClose } from "react-icons/md";
import { SharpRatioString } from "@/helpers/types";
import { BadgeAlert, BadgeCheck } from "lucide-react";
const TrendingStrategies = ({
  userInfo,
  t,
  show,
  setShow,
  logos,
  darkTheme,
  getNameInitials,
}: any) => {
  const router = useRouter();
  const { toast } = useToast();
  const [authBearer, setAuthBearer] = useState({
    headers: {
      Authorization: `Bearer ${userInfo?.token}`,
    },
  });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [strategiesLoader, setStrategiesLoader] = useState(false);
  const [strategyData, setStrategyData] = useState<any[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [selected, setSelected] = useState<any>({});
  const [selectedFilter, setSelectedFilter] = useState<string>("");
  const settings = {
    className: "variable-width",
    arrows: true,
    dots: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    speed: 500,
    initialSlide: 0,
    infinite: false,
  };

  const getTrendingStrategiesData = () => {
    setStrategiesLoader(true);
    let url = new URL("get_trending_strategies", API_URL);
    url.searchParams.append("user_id", userInfo?.user_id);
    url.searchParams.append("offset", currentPage?.toString());
    url.searchParams.append("asset_class", selectedFilter);
    axios
      .get(url.toString(), authBearer)
      .then((res: any) => {
        if (res?.status === 200) {
          const newArray = Object.keys(res.data)?.map((key) => ({
            ...res?.data[key],
          }));
          setStrategyData(newArray);
          setTotalResults(newArray?.length);
        }
      })
      .catch((e) => {
        setStrategiesLoader(true);
        console.error(e);
      })
      .finally(() => setStrategiesLoader(false));
  };

  const copyHandler = useCallback((key: string) => {
    let url = new URL("copy_strategy", API_URL);
    url.searchParams.append("user_id", userInfo?.user_id);
    url.searchParams.append("strategy_key", key);
    axios
      .post(
        url.toString(),
        {},
        {
          headers: {
            Authorization: `Bearer ${userInfo?.token}`,
          },
        }
      )
      .then((res) => {
        if (res?.data?.keygen) {
          toast({
            title: t("Strategy copied successfully"),
            variant: "default",
          });
          setTimeout(() => {
            router.push(
              "/edit-strategy/" +
                encodeURIComponent(userInfo?.user_id) +
                "/" +
                res?.data?.id +
                randomString(5) +
                "/" +
                userInfo.token +
                "/false" +
                `?ref=${router.asPath}&locale=${router.query.locale || "en"}`
            );
          }, 2000);
        } else {
          toast({
            title: t("Error copying strategy"),
            variant: "destructive",
          });
        }
      })
      .catch((e) => {
        toast({
          title: t("Error copying strategy"),
          variant: "destructive",
        });
      });
  }, []);

  useEffect(() => {
    show && getTrendingStrategiesData();
  }, [userInfo, show, currentPage, selectedFilter]);

  return (
    <>
      <ViewModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        selected={selected}
        token={userInfo?.token}
      />
      <Drawer open={show} onOpenChange={setShow}>
        <DrawerContent>
          <Card className="mt-3">
            <div className="w-full h-[calc(100vh-100px)]  build-page flex flex-col">
              {strategiesLoader ? (
                <div className="h-full w-full flex justify-center items-center">
                  <Loader />
                </div>
              ) : (
                <>
                  <CardTitle className="text-2xl p-2 justify-between flex gap-2 items-center font-bold tracking-tight">
                    {t("Trending Strategies")}
                    <Button variant={"outline"} onClick={() => setShow(false)}>
                      <IoClose className="!text-2xl cursor-pointer" />
                    </Button>
                  </CardTitle>
                  <div className="flex gap-4 items-center px-4 mb-4">
                    <IoFilterSharp />
                    <Badge
                      variant={
                        selectedFilter === "forex" ? "default" : "outline"
                      }
                      onClick={() => setSelectedFilter("forex")}
                      className="cursor-pointer"
                    >
                      {t("Forex")}
                    </Badge>
                    <Badge
                      variant={
                        selectedFilter === "crypto" ? "default" : "outline"
                      }
                      onClick={() => setSelectedFilter("crypto")}
                      className="cursor-pointer"
                    >
                      {t("Crypto")}
                    </Badge>
                    <Badge
                      variant={
                        selectedFilter === "equity" ? "default" : "outline"
                      }
                      onClick={() => setSelectedFilter("equity")}
                      className="cursor-pointer"
                    >
                      {t("Equities")}
                    </Badge>
                    {selectedFilter && (
                      <MdClose
                        className="cursor-pointer"
                        onClick={() => setSelectedFilter("")}
                      />
                    )}
                  </div>
                  <ScrollArea className="h-full pr-6 p-4 overflow-auto mt-2">
                    {strategyData?.map((item: any, index: number) => {
                      let badges = [
                        item?.strategy_meta?.actions,
                        item?.strategy_meta?.datas,
                        item?.strategy_meta?.indicators,
                        item?.strategy_meta?.logics,
                      ].reduce((acc, curr) => {
                        return acc.concat(curr);
                      }, []);
                      const canvasData = JSON.parse(
                        item?.strategy_meta?.canvas_data
                      );
                      const nodes = canvasData?.filter?.(
                        (item: any) => item.type === "selectorNode"
                      );
                      const edges = canvasData?.filter?.(
                        (item: any) => item.type === "buttonedge"
                      );
                      const isFxStartegy = !getIsFxStrategy(item?.symbol);
                      return (
                        <Card
                          key={index}
                          className={cn("h-fit", index != 0 ? "mt-2" : "")}
                        >
                          <CardHeader className="p-4">
                            <CardTitle className="text-md flex items-center gap-2">
                              {logos.get(item.symbol) !== undefined ? (
                                <img
                                  src={logos.get(item.symbol)}
                                  style={{
                                    maxHeight: "30px",
                                    maxWidth: "30px",
                                  }}
                                  alt={item.symbol}
                                />
                              ) : (
                                <img
                                  src={
                                    darkTheme
                                      ? "/images/logos/Symbol_Dark.png"
                                      : "/images/logos/Symbol_Light-30.png"
                                  }
                                  style={{
                                    maxHeight: "30px",
                                    maxWidth: "30px",
                                  }}
                                  alt={item.symbol}
                                />
                              )}
                              <div className="text-muted-foreground">
                                {item.symbol}
                              </div>

                              <div className="pl-1 text-[12px] text-muted-foreground">
                                {item.timeframe}
                              </div>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-4 pt-0 flex flex-col gap-3">
                            <div className="relative w-full h-2">
                              <div className="absolute top-0 left-6 right-6 bottom-0">
                                <Slider {...settings}>
                                  {badges?.map((item: any, id: number) => {
                                    return (
                                      <div className="mr-2" key={id}>
                                        <Badge variant="outline">{item}</Badge>
                                      </div>
                                    );
                                  })}
                                </Slider>
                              </div>
                            </div>
                            <div className="w-full max-w-sm ml-6"></div>
                            <div className="border-b flex flex-col gap-2 pb-2">
                              <div className="text-muted-foreground text-sm font-bold">
                                {t("Statistics")}
                              </div>
                              <div className="flex text-xs gap-8 items-center">
                                <div className="flex flex-col items-center gap-1">
                                  <div className="flex items-center gap-0.5">
                                    {t("Drawdown")}{" "}
                                    <HoverCard>
                                      <HoverCardTrigger>
                                        <BiSolidHelpCircle size={16} />
                                      </HoverCardTrigger>
                                      <HoverCardContent>
                                        {t(
                                          "How much your strategy drops from its highest point to its lowest to measure risk - smaller drops mean less risk, bigger drops mean more risk."
                                        )}
                                      </HoverCardContent>
                                    </HoverCard>
                                  </div>
                                  <div>
                                    $
                                    {formatToDecimalPlaces(
                                      item?.strategy_stats?.dd,
                                      2
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                  <div className="flex items-center gap-0.5">
                                    {t("Sharpe Ratio")}
                                    <HoverCard>
                                      <HoverCardTrigger>
                                        <BiSolidHelpCircle size={16} />
                                      </HoverCardTrigger>
                                      <HoverCardContent className="flex flex-col gap-1">
                                        {t(
                                          "The risk-adjusted return of your strategy, helping you assess the quality of your trading strategy overall."
                                        )}
                                        <span>
                                          {t(
                                            "<1: Poor – Returns are not compensating for the risk taken."
                                          )}
                                        </span>
                                        <span>
                                          {t(
                                            "1: Good – Acceptable risk-adjusted returns."
                                          )}
                                        </span>
                                        <span>
                                          {t(
                                            "2: Very Good – Strong performance with solid risk management."
                                          )}
                                        </span>
                                        <span>
                                          {t(
                                            ">3: Excellent – Exceptional risk-adjusted returns."
                                          )}
                                        </span>
                                      </HoverCardContent>
                                    </HoverCard>
                                  </div>
                                  <div
                                    className={cn(
                                      "",
                                      item?.strategy_stats?.sr ===
                                        SharpRatioString.BELOW3
                                        ? "text-red-foreground"
                                        : item?.strategy_stats?.sr ===
                                          SharpRatioString.ABOVE3
                                        ? "text-green-foreground"
                                        : "text-foreground"
                                    )}
                                  >
                                    {item?.strategy_stats?.sr}
                                  </div>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                  <div className="flex items-center gap-0.5">
                                    {t("Win")}%
                                    <HoverCard>
                                      <HoverCardTrigger>
                                        <BiSolidHelpCircle size={16} />
                                      </HoverCardTrigger>
                                      <HoverCardContent>
                                        {t(
                                          "The percentage of trades that ended in profit. A higher Win % means more successful trades."
                                        )}
                                      </HoverCardContent>
                                    </HoverCard>
                                  </div>
                                  <div
                                    className={cn(
                                      "",
                                      item?.strategy_stats?.win > 0
                                        ? "text-green-foreground"
                                        : item?.strategy_stats?.win === 0
                                        ? "text-foreground"
                                        : "text-red-foreground"
                                    )}
                                  >
                                    {formatToDecimalPlaces(
                                      item?.strategy_stats?.win,
                                      2
                                    )}
                                    %
                                  </div>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                  <div className="flex items-center gap-0.5">
                                    {t("Copier Count")}
                                    {/* <HoverCard>
                                      <HoverCardTrigger>
                                        <BiSolidHelpCircle size={16} />
                                      </HoverCardTrigger>
                                      <HoverCardContent>
                                        {t("The percentage of trades that ended in profit. A higher Win % means more successful trades.")}
                                      </HoverCardContent>
                                    </HoverCard> */}
                                  </div>
                                  <div className="text-foreground">
                                    {item?.copier_count}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                          <CardFooter className="flex justify-between pb-4">
                            <div>
                              {/* <div className="text-sm text-muted-foreground">
                                {t("By")}{" "}
                                <Button
                                  variant="link"
                                  className="h-fit w-fit p-0"
                                  onClick={() =>
                                    router.push(
                                      `/profile/${item.unique_key}?locale=${
                                        router.query.locale || "en"
                                      }`
                                    )
                                  }
                                >
                                  {item.first_name} {item.last_name}
                                </Button>
                              </div> */}
                              <div className="text-sm text-muted-foreground flex items-center mb-[2px]">
                                {/* {t("By")}{" "} */}

                                <div
                                  className={cn(
                                    "flex h-full w-full items-center justify-center rounded-full bg-muted w-[27px] h-[27px] mr-2"
                                  )}
                                >
                                  {getNameInitials(
                                    item.first_name + " " + item.last_name
                                  )}
                                </div>

                                <Button
                                  variant="link"
                                  className="h-fit w-fit p-0 text-sm font-semibold"
                                  onClick={() =>
                                    router.push(
                                      `/profile/${item.unique_key}?locale=${
                                        router.query.locale || "en"
                                      }`
                                    )
                                  }
                                >
                                  {item.first_name} {item.last_name}
                                  <BadgeCheck className="text-[#FFD700] w-[16px] h-[16px] ml-2" />
                                </Button>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {t("Member since")}{" "}
                                {formatDate(item.created_at, "DD-MMM-YY")}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <div
                                className="mr-2 text-sm font-bold cursor-pointer text-muted-foreground hover:text-[#545454]"
                                onClick={() => {
                                  setSelected({
                                    nodes,
                                    edges,
                                    symbol: item.symbol,
                                    timeframe: item.timeframe,
                                  });
                                  setShowModal(true);
                                }}
                              >
                                <div className="flex items-center">
                                  <div>
                                    <Eye className="w-[15px]" />
                                  </div>
                                  <div className="ml-1">{t("View")}</div>
                                </div>
                              </div>
                              {isFxStartegy ? (
                                <div
                                  className="text-sm font-bold cursor-pointer text-muted-foreground hover:text-[#545454]"
                                  onClick={() => copyHandler(item?.keygen)}
                                >
                                  <div className="flex items-center">
                                    <div>
                                      <Layers2 className="w-[15px]" />
                                    </div>
                                    <div className="ml-1">{t("Copy")}</div>
                                  </div>
                                </div>
                              ) : (
                                <HoverCard>
                                  <HoverCardTrigger>
                                    <div className="text-sm font-bold cursor-pointer text-muted hover:text-[#545454]">
                                      <div className="flex items-center">
                                        <div>
                                          <Layers2 className="w-[15px]" />
                                        </div>
                                        <div className="ml-1">{t("Copy")}</div>
                                      </div>
                                    </div>
                                  </HoverCardTrigger>
                                  <HoverCardContent className="w-40">
                                    {t(
                                      "This asset class is not currently accessible for your account"
                                    )}
                                  </HoverCardContent>
                                </HoverCard>
                              )}
                            </div>
                          </CardFooter>
                        </Card>
                      );
                    })}
                    <ScrollBar orientation="vertical" />
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </>
              )}
              <div className="pb-4">
                <PaginationButton
                  currentPage={currentPage}
                  totalResults={totalResults}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </div>
          </Card>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default TrendingStrategies;
